import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Sidebar } from './Sidebar';
import { MobileNav } from './MobileNav';
import { Toaster } from '../ui/sonner';
import { X } from 'lucide-react';
import { OnboardingTour, HelpCenter } from '../onboarding';

// Rutas operativas que requieren marca seleccionada para Super Admin
const operativeRoutes = [
  '/dashboard',
  '/facturacion',
  '/movimientos',
  '/categorias',
  '/clientes-pagos',
  '/flujo-caja',
  '/estado-resultados',
  '/pagos',
  '/prioridades',
  '/rentabilidad',
  // '/configuracion', // Removed - only super admin can add users
  // Premium modules
  '/presupuestos',
  '/forecast',
  '/proyectos',
  '/equipo',
  '/reportes-auto',
  '/ai-advisor'
];

export const Layout = () => {
  const { user, loading, isSuperAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Redirect Super Admin to /admin if trying to access operative routes without selected marca
  useEffect(() => {
    if (isSuperAdmin && operativeRoutes.includes(location.pathname)) {
      const marcaId = localStorage.getItem('selectedMarcaId');
      if (!marcaId) {
        navigate('/admin', { replace: true });
      }
    }
  }, [location.pathname, isSuperAdmin, navigate]);

  // Check onboarding status and start tour if needed
  useEffect(() => {
    if (user && !user.onboarding_completado && !isSuperAdmin) {
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        setRunTour(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, isSuperAdmin]);

  // Close sidebar when clicking outside on mobile
  const handleOverlayClick = () => {
    setSidebarOpen(false);
  };

  const handleTourComplete = () => {
    setRunTour(false);
  };

  const handleStartTour = () => {
    // Navigate to dashboard first if not already there
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
    setTimeout(() => setRunTour(true), 500);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-layout">
      {/* Desktop Sidebar */}
      <aside className="desktop-sidebar" data-testid="sidebar">
        <Sidebar />
      </aside>

      {/* Mobile Sidebar Drawer */}
      <div className={`mobile-sidebar-overlay ${sidebarOpen ? 'active' : ''}`} onClick={handleOverlayClick} />
      <aside className={`mobile-sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="mobile-sidebar">
        <div className="flex justify-end p-3 flex-shrink-0 border-b bg-white sticky top-0 z-10">
          <button 
            onClick={() => setSidebarOpen(false)} 
            className="p-2 rounded-lg hover:bg-slate-100"
            aria-label="Cerrar menú"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Sidebar />
        </div>
      </aside>

      {/* Main content */}
      <main className="app-main">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileNav onMenuClick={() => setSidebarOpen(true)} />

      {/* Onboarding Tour */}
      <OnboardingTour run={runTour} onComplete={handleTourComplete} />

      {/* Help Center (floating button) - only show for non-super-admin in operative views */}
      {!isSuperAdmin && <HelpCenter onStartTour={handleStartTour} />}

      <Toaster position="top-center" richColors />
    </div>
  );
};
